import {
  NetClock,
} from "./clock.js";
import type {
  EntitySnap,
  Snapshot,
  Stance,
  TacticalKind,
  Vec3,
  WildlifeSpecies,
} from "@rietz/shared";

/**
 * Entity interpolation: remotes are rendered `INTERP_DELAY_MS` in the past so
 * there is always a pair of samples to blend, stamped on the TICK timeline
 * (`tick * TICK_DT * 1000`), never arrival time. Underrun extrapolates along
 * the last velocity for at most `MAX_EXTRAPOLATE_MS`, then freezes; a stale
 * entity is dropped after `ENTITY_TIMEOUT_MS`; `playerLeft` drops immediately.
 */

/** How far ahead of the newest sample we are willing to guess. */
const MAX_EXTRAPOLATE_MS = 100;

/** Samples older than this are discarded. */
const HISTORY_MS = 1000;

/** An entity unseen for this long is considered gone. */
const ENTITY_TIMEOUT_MS = 1000;

/** Per-entity sample cap; a hard bound on memory if timestamps go haywire. */
const MAX_SAMPLES = 32;

interface Sample {
  /** Timestamp on the server tick timeline, ms. */
  t: number;
  pos: Vec3;
  yaw: number;
  pitch: number;
  stance: Stance;
  anim: EntitySnap["anim"];
  reload: boolean;
  lean: -1 | 0 | 1;
  tac: TacticalKind | null;
}

/**
 * What an entity is visibly wearing. Never interpolated — a helmet is on or
 * off.
 */
export type PoseGear = NonNullable<EntitySnap["gear"]>;

/** The interpolated pose handed to the renderer. */
export interface InterpPose {
  id: string;
  kind: EntitySnap["kind"];
  name: string;
  /**
   * The scav's role, or `undefined` for a Wilderer or a human. A TRACK fact
   * like `name`: sent once, omitted after.
   */
  role?: EntitySnap["role"];
  /**
   * Which animal this is, or `undefined` for a man. A TRACK fact: the rig is
   * built once and a species never changes, and an extrapolated pose is still a
   * hare.
   */
  species?: WildlifeSpecies;
  pos: Vec3;
  yaw: number;
  pitch: number;
  stance: Stance;
  anim: EntitySnap["anim"];
  /**
   * Visible gear, or `null` when the server has not said — the avatar keeps its
   * default silhouette.
   */
  gear: PoseGear | null;
  /**
   * Mid-magazine-change. A SAMPLE field, unlike `gear`: a state with a start
   * and end that must line up with its pose. Snapped at the segment's halfway
   * point, like `stance`.
   */
  reload: boolean;
  /**
   * Peek direction: −1, 0, +1. A SAMPLE field snapped at halfway — an
   * in-between is not a state the server had, and it is the `HitPose.lean`
   * client hit prediction reads by reference.
   */
  lean: -1 | 0 | 1;
  /**
   * The mounted device switched on, or `null`. A SAMPLE field snapped at
   * halfway, like `reload` and `lean` — nothing lies between a torch and a
   * laser.
   */
  tac: TacticalKind | null;
  /**
   * In our own squad. A TRACK field (who a man deploys with does not change
   * inside the buffer), but BELIEVED rather than held: the server sends it on
   * every snapshot a teammate appears in, so silence means "not a teammate".
   */
  team: boolean;
  /** True while the pose is being guessed past the newest sample. */
  extrapolated: boolean;
}

class Track {
  readonly id: string;
  kind: EntitySnap["kind"] = "player";
  name = "";
  /** Last role seen, held the way `name` is — see `InterpPose.role`. */
  role: EntitySnap["role"] = undefined;
  /** Last species seen, held the same way — see `InterpPose.species`. */
  species: WildlifeSpecies | undefined = undefined;
  /**
   * Latest gear seen, on the TRACK like `name`: discrete entity state, not a
   * blend.
   */
  gear: PoseGear | null = null;
  /** Whether the last snapshot said this entity is one of ours. See
   *  `InterpPose.team` for why it is re-read rather than remembered. */
  team = false;
  /** Newest tick-timeline stamp at which this entity appeared. */
  lastSeenMs = 0;

  readonly samples: Sample[] = [];
  readonly pose: InterpPose;

  constructor(id: string) {
    this.id = id;
    this.pose = {
      id,
      kind: "player",
      name: "",
      role: undefined,
      species: undefined,
      pos: { x: 0, y: 0, z: 0 },
      yaw: 0,
      pitch: 0,
      stance: "stand",
      anim: "idle",
      gear: null,
      reload: false,
      lean: 0,
      tac: null,
      team: false,
      extrapolated: false,
    };
  }

  ingest(e: EntitySnap, t: number): void {
    this.kind = e.kind;
    if (e.name !== undefined && e.name.length > 0) this.name = e.name;
    if (e.role !== undefined) this.role = e.role;
    // Held rather than cleared, like `role`: a quiet animal is still the animal
    // it was.
    if (e.species !== undefined) this.species = e.species;
    // A snapshot without gear leaves the last known gear alone — a corpse's kit
    // stays on it.
    if (e.gear !== undefined) this.gear = e.gear;
    // ASSIGNED UNCONDITIONALLY: the field is omitted at false and re-sent every
    // snapshot, so absent means "no". Held, a stale diamond would outlive its
    // vouching.
    this.team = e.team === true;
    this.lastSeenMs = t;

    const last = this.samples[this.samples.length - 1];
    if (last !== undefined && t <= last.t) {
      // Out-of-order or duplicate snapshot: overwrite the newest sample rather
      // than corrupting the buffer's ordering invariant.
      if (last.t === t) {
        last.pos.x = e.pos.x;
        last.pos.y = e.pos.y;
        last.pos.z = e.pos.z;
        last.yaw = e.yaw;
        last.pitch = e.pitch;
        last.stance = e.stance;
        last.anim = e.anim;
        last.reload = e.reload === true;
        last.lean = e.lean ?? 0;
        last.tac = e.tac ?? null;
      }
      return;
    }

    this.samples.push({
      t,
      pos: { x: e.pos.x, y: e.pos.y, z: e.pos.z },
      yaw: e.yaw,
      pitch: e.pitch,
      stance: e.stance,
      anim: e.anim,
      // Absent means "not reloading": the field is omitted whenever it would be
      // false, and a pre-v16 server never sends it at all.
      reload: e.reload === true,
      // Likewise absent means upright — omitted at 0, and unknown to anything
      // older than v17.
      lean: e.lean ?? 0,
      // ...and absent means dark, omitted whenever there is no light and unknown
      // to anything older than v20.
      tac: e.tac ?? null,
    });

    if (this.samples.length > MAX_SAMPLES) this.samples.shift();
  }

  prune(newestMs: number): void {
    // Always keep the two newest samples, whatever their age — they are what a
    // frozen/extrapolated pose falls back to.
    while (
      this.samples.length > 2 &&
      (this.samples[1] as Sample).t < newestMs - HISTORY_MS
    ) {
      this.samples.shift();
    }
  }

  /** Writes the pose for `renderMs`. Returns `false` when there is no data. */
  sample(renderMs: number): boolean {
    const n = this.samples.length;
    if (n === 0) return false;

    const pose = this.pose;
    pose.kind = this.kind;
    pose.name = this.name;
    pose.role = this.role;
    pose.species = this.species;
    pose.gear = this.gear;
    pose.team = this.team;

    const newest = this.samples[n - 1] as Sample;
    const oldest = this.samples[0] as Sample;

    if (renderMs <= oldest.t) {
      // Behind our history (fresh entity, or a big clock resync): hold.
      writePose(pose, oldest, oldest, 0);
      pose.extrapolated = false;
      return true;
    }

    if (renderMs >= newest.t) {
      // Buffer underrun. Continue along the last known velocity, briefly.
      const ahead = Math.min(renderMs - newest.t, MAX_EXTRAPOLATE_MS);
      const prev = n >= 2 ? (this.samples[n - 2] as Sample) : null;
      pose.stance = newest.stance;
      pose.anim = newest.anim;
      pose.reload = newest.reload;
      pose.lean = newest.lean;
      pose.tac = newest.tac;
      pose.yaw = newest.yaw;
      pose.pitch = newest.pitch;
      if (prev === null || newest.t - prev.t <= 0 || ahead <= 0) {
        pose.pos.x = newest.pos.x;
        pose.pos.y = newest.pos.y;
        pose.pos.z = newest.pos.z;
        pose.extrapolated = false;
        return true;
      }
      const inv = 1 / (newest.t - prev.t);
      pose.pos.x = newest.pos.x + (newest.pos.x - prev.pos.x) * inv * ahead;
      pose.pos.y = newest.pos.y + (newest.pos.y - prev.pos.y) * inv * ahead;
      pose.pos.z = newest.pos.z + (newest.pos.z - prev.pos.z) * inv * ahead;
      pose.extrapolated = true;
      return true;
    }

    // Normal case: find the bracketing pair. Scanning backwards is cheapest
    // because `renderMs` is nearly always inside the last segment.
    for (let i = n - 1; i > 0; i--) {
      const b = this.samples[i] as Sample;
      const a = this.samples[i - 1] as Sample;
      if (renderMs >= a.t && renderMs <= b.t) {
        const span = b.t - a.t;
        const t = span > 0 ? (renderMs - a.t) / span : 0;
        writePose(pose, a, b, t);
        pose.extrapolated = false;
        return true;
      }
    }

    writePose(pose, newest, newest, 0);
    pose.extrapolated = false;
    return true;
  }
}

function writePose(pose: InterpPose, a: Sample, b: Sample, t: number): void {
  pose.pos.x = a.pos.x + (b.pos.x - a.pos.x) * t;
  pose.pos.y = a.pos.y + (b.pos.y - a.pos.y) * t;
  pose.pos.z = a.pos.z + (b.pos.z - a.pos.z) * t;
  pose.yaw = lerpAngle(a.yaw, b.yaw, t);
  pose.pitch = a.pitch + (b.pitch - a.pitch) * t;
  // Stance, anim, the reload flag, the lean and the device are discrete: switch
  // at the halfway point rather than inventing an in-between value.
  pose.stance = t < 0.5 ? a.stance : b.stance;
  pose.anim = t < 0.5 ? a.anim : b.anim;
  pose.reload = t < 0.5 ? a.reload : b.reload;
  pose.lean = t < 0.5 ? a.lean : b.lean;
  pose.tac = t < 0.5 ? a.tac : b.tac;
}

/** Shortest-arc angular lerp — turning past π must not spin the long way. */
export function lerpAngle(a: number, b: number, t: number): number {
  const TWO_PI = Math.PI * 2;
  let d = (b - a) % TWO_PI;
  if (d > Math.PI) d -= TWO_PI;
  else if (d < -Math.PI) d += TWO_PI;
  return a + d * t;
}

export class EntityInterpolator {
  private readonly tracks = new Map<string, Track>();
  private readonly poses: InterpPose[] = [];

  /** Newest snapshot timestamp seen, on the tick timeline. */
  private newestMs = 0;

  /** Local `performance.now()` at which the newest snapshot arrived. */
  lastSnapshotAtMs = 0;

  get entityCount(): number {
    return this.tracks.size;
  }

  /**
   * The poses written by the most recent `sample()` — exactly what is on
   * screen, and what client hit PREDICTION must test against. Same reused
   * array; do not retain.
   */
  get lastPoses(): readonly InterpPose[] {
    return this.poses;
  }

  ingest(snap: Snapshot, arrivedAtMs: number): void {
    const t = NetClock.tickToMs(snap.tick);
    if (t > this.newestMs) this.newestMs = t;
    this.lastSnapshotAtMs = arrivedAtMs;

    for (const e of snap.entities) {
      let track = this.tracks.get(e.id);
      if (track === undefined) {
        track = new Track(e.id);
        this.tracks.set(e.id, track);
      }
      track.ingest(e, t);
    }

    // An entity that stopped appearing in snapshots without a `playerLeft`
    // (server crash, dropped packet, out-of-interest culling) is timed out.
    for (const [id, track] of this.tracks) {
      if (this.newestMs - track.lastSeenMs > ENTITY_TIMEOUT_MS) {
        this.tracks.delete(id);
        continue;
      }
      track.prune(this.newestMs);
    }
  }

  remove(id: string): void {
    this.tracks.delete(id);
  }

  clear(): void {
    this.tracks.clear();
    this.newestMs = 0;
  }

  /**
   * Poses for `renderMs` (already delayed by the caller). The array is reused —
   * do not retain it.
   */
  sample(renderMs: number): readonly InterpPose[] {
    this.poses.length = 0;
    for (const track of this.tracks.values()) {
      if (track.sample(renderMs)) this.poses.push(track.pose);
    }
    return this.poses;
  }
}
